import { useNavigate } from "react-router-dom";
import { CalendarCheck, Heart, Bell, Question, FileText, ShieldCheck, SignOut, CaretRight, PencilSimple, Gift, LockKey, Trash, WarningCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const items = [
  { label: "My Bookings", icon: CalendarCheck, to: "/bookings" },
  { label: "Payment History", icon: FileText, to: "/payments" },
  { label: "Favorites", icon: Heart, to: "/favorites" },
  { label: "Refer & Earn", icon: Gift, to: "/refer" },
  { label: "Notifications", icon: Bell, to: "/notifications" },
  { label: "Help & Support", icon: Question, to: "/support" },
  { label: "Terms & Conditions", icon: FileText, to: "/terms" },
  { label: "Privacy Policy", icon: ShieldCheck, to: "/privacy" },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [open, setOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { password: delPw, confirm: delConfirm });
      toast.success("Your account has been deleted.");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account.");
    } finally { setDeleting(false); }
  };

  const save = async () => {
    const r = await api.put("/auth/profile", { name, phone });
    updateUser(r.data);
    toast.success("Profile updated");
    setOpen(false);
  };

  return (
    <div className="px-4 pt-5">
      <h1 className="font-heading text-2xl font-semibold text-stone-900">Profile</h1>

      <div className="mt-4 flex items-center gap-4 rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-white">
          {user?.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <p className="font-heading text-lg font-semibold text-stone-900">{user?.name}</p>
          <p className="text-sm text-stone-500">{user?.email}</p>
          <p className="text-sm text-stone-500">{user?.phone}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="edit-profile" className="rounded-full border border-stone-200 p-2 hover:bg-accent"><PencilSimple size={18} /></button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Edit Profile</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input data-testid="edit-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label>Phone</Label><Input data-testid="edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-xl" /></div>
              <Button data-testid="save-profile" onClick={save} className="w-full rounded-xl bg-primary hover:bg-green-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-4 divide-y divide-stone-100 overflow-hidden rounded-2xl border border-stone-200 bg-white">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.label} onClick={() => navigate(it.to)} className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-accent">
              <Icon size={20} weight="duotone" className="text-primary" />
              <span className="flex-1 text-sm font-medium text-stone-800">{it.label}</span>
              <CaretRight size={16} className="text-stone-300" />
            </button>
          );
        })}
      </div>

      <button data-testid="open-change-password" onClick={() => setCpOpen(true)} className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white px-5 py-4 text-left transition-colors hover:bg-accent">
        <LockKey size={20} weight="duotone" className="text-primary" />
        <span className="flex-1 text-sm font-medium text-stone-800">Change Password</span>
        <CaretRight size={16} className="text-stone-300" />
      </button>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />

      <Button data-testid="profile-logout" variant="outline" className="mt-5 w-full gap-2 rounded-xl text-destructive hover:bg-red-50" onClick={() => { logout(); navigate("/"); }}>
        <SignOut size={18} /> Logout
      </Button>

      <button data-testid="open-delete-account" onClick={() => setDelOpen(true)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium text-stone-400 hover:text-destructive">
        <Trash size={16} /> Delete my account
      </button>
      <Dialog open={delOpen} onOpenChange={(v) => { if (!v) { setDelPw(""); setDelConfirm(""); } setDelOpen(v); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><WarningCircle size={20} weight="fill" className="text-red-500" /> Delete your account?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-stone-600">
            <p>This permanently closes your account and removes your personal details. For legal and financial reasons, records of past bookings and payments are retained in anonymised form. This cannot be undone.</p>
            <div><Label>Confirm your password</Label><Input data-testid="delete-password" type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} className="mt-1 rounded-xl" placeholder="••••••••" /></div>
            <div><Label>Type <span className="rounded bg-stone-100 px-1.5 py-0.5 font-mono font-bold text-red-600">DELETE</span> to confirm</Label><Input data-testid="delete-confirm" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} className="mt-1 rounded-xl" placeholder="DELETE" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => { setDelOpen(false); setDelPw(""); setDelConfirm(""); }}>Cancel</Button>
            <Button data-testid="confirm-delete-account" disabled={!delPw || delConfirm !== "DELETE" || deleting} className="rounded-xl bg-red-600 hover:bg-red-700" onClick={deleteAccount}>{deleting ? "Deleting…" : "Delete Account"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <p className="mt-6 text-center text-xs text-stone-400">Avinya Bookings · Book. Relax. Celebrate.</p>
    </div>
  );
}
